import { ENDPOINTS, TASK_TYPES } from 'features/daily-plan/constants';
import { Permissions, userHasPermission } from 'services/Authorization';
import CandidatesTab from 'UI/components/organisms/inventoryProfiles/CandidatesTab';
import {
  candidateColumns,
  candidateVirtualProps,
  CustomCandidateRenders
} from 'UI/components/organisms/inventoryProfiles/CandidatesTab/columns';
import HiringAuthorityTab from 'UI/components/organisms/inventoryProfiles/HiringAuthorityTab';
import {
  HiringAuthorityVirtualProps,
  jobOrderProfileHAColumns
} from 'UI/components/organisms/inventoryProfiles/HiringAuthorityTab/jobOrderProfileHAColumns';
import {
  JOB_ORDER_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE,
  JOB_ORDER_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE,
  MatchMakingTab
} from 'UI/components/organisms/inventoryProfiles/MatchMakingTab';
import PlacementsTab from 'UI/components/organisms/inventoryProfiles/PlacementsTab';
import {
  IncludesByMode,
  jobOrderProfilePlacementsColumns
} from 'UI/components/organisms/inventoryProfiles/PlacementsTab/columns';
import { handleHiringAuthorityResponse } from 'UI/components/organisms/inventoryProfiles/responseChange';
import SendoutsTab from 'UI/components/organisms/inventoryProfiles/SendoutsTab';
import {
  SENDOUTS_COLUMNS,
  SENDOUTS_RENDERERS,
  SENDOUTS_VIRTUAL_PROPS
} from 'UI/components/organisms/inventoryProfiles/SendoutsTab/columns';
import SendoutsTabV2 from 'UI/components/organisms/inventoryProfiles/SendoutsTab/SendoutsTabV2';
import { ProfileTablesPreferenceKeys } from 'UI/components/organisms/inventoryProfiles/tabKeys';
import {
  formatTasksResponse,
  TASK_COLUMNS,
  TASKS_EXTRA_RENDERERS,
  TasksTab
} from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { JobDescriptionViewer } from 'UI/components/organisms/inventoryProfiles/viewers';
import { Endpoints } from 'UI/constants/endpoints';
import {
  ApplicantEntity,
  CandidateEntity,
  ContactEntity,
  ContactRole,
  ENTITY_TYPE_ID_MAP,
  EntityType,
  TabKeys
} from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { hasFeatureFlag } from 'UI/utils';

const entityTypesForMatchMaking = [
  {
    columns: JOB_ORDER_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Candidate],
    endpoint: Endpoints.JobOrderMatchCandidate,
    formatResponse: JOB_ORDER_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Candidate],
    id: ENTITY_TYPE_ID_MAP.candidate,
    isEnabled: hasFeatureFlag(FeatureFlags.JobOrderMatchMakingByCandidate),
    secondaryEnpoint: Endpoints.JobOrderAIMatchCandidate,
    singular: CandidateEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersMatchCandidate,
    title: CandidateEntity.title,
    type: EntityType.Candidate
  },
  {
    columns: JOB_ORDER_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Applicant],
    disableTableActions: true,
    formatResponse: JOB_ORDER_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Applicant],
    id: ENTITY_TYPE_ID_MAP.applicant,
    isEnabled: hasFeatureFlag(FeatureFlags.JobOrderMatchMakingByApplicant),
    secondaryEnpoint: Endpoints.JobOrderAIMatchApplicants,
    singular: ApplicantEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersMatchApplicant,
    title: ApplicantEntity.title,
    type: EntityType.Applicant
  },
  {
    columns: JOB_ORDER_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Contact],
    formatResponse: JOB_ORDER_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Contact],
    id: ContactRole.Name,
    isEnabled: hasFeatureFlag(FeatureFlags.JobOrderMatchMakingByContact),
    matchedParamKey: 'matchedJobOrder',
    secondaryEnpoint: Endpoints.JobOrderAIMatchContacts,
    singular: ContactEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersMatchContact,
    title: ContactEntity.title,
    type: EntityType.Contact
  }
];

const enabledEntityTypesForMatchMaking = entityTypesForMatchMaking.filter(
  entity => entity.isEnabled
);

export const jobOrdersTabDefinition = [
  {
    id: TabKeys.JobDescription,
    label: 'Job Description',
    Component: JobDescriptionViewer,
    visible: () => hasFeatureFlag(FeatureFlags.JobOrderJobDescription),
    props: {}
  },
  {
    id: TabKeys.MatchMaking,
    label: 'Match Making',
    Component: MatchMakingTab,
    props: {
      availableEntityTypesToMatch: enabledEntityTypesForMatchMaking,
      DataTableProps: {
        apiVersion: 1
      }
    }
  },
  {
    id: TabKeys.Sendouts,
    label: 'Sendouts',
    endpoint: Endpoints.Sendouts,
    Component: SendoutsTab,
    visible: () => userHasPermission(Permissions.Sendouts.CanUseModule),
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersSendouts,
      extraRenderers: SENDOUTS_RENDERERS,
      initialColumns: SENDOUTS_COLUMNS,
      DataTableProps: {
        apiVersion: 2,
        virtualProps: SENDOUTS_VIRTUAL_PROPS,
        includes:
          'type,status,interviews,hiringAuthorithies,jobOrderAccountable,candidateAccountable,joborder,candidate,jobOrderAccountableCoach,candidateAccountableCoach'
      }
    }
  },
  {
    id: TabKeys.SendoutsV2,
    label: 'Sendouts V2',
    endpoint: Endpoints.Sendouts,
    Component: SendoutsTabV2,
    visible: () =>
      userHasPermission(Permissions.Sendouts.CanUseModule) &&
      hasFeatureFlag(FeatureFlags.SendoutsV2),
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersSendouts,
      extraRenderers: SENDOUTS_RENDERERS,
      initialColumns: SENDOUTS_COLUMNS,
      DataTableProps: {
        apiVersion: 2,
        virtualProps: SENDOUTS_VIRTUAL_PROPS,
        includes:
          'type,status,interviews,hiringAuthorithies,jobOrderAccountable,candidateAccountable,joborder,candidate,jobOrderAccountableCoach,candidateAccountableCoach'
      }
    }
  },
  {
    id: TabKeys.Placements,
    label: 'Placements',
    endpoint: Endpoints.Placements,
    Component: PlacementsTab,
    visible: () => userHasPermission(Permissions.Placements.CanUseModule),
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersPlacements,
      initialColumns: jobOrderProfilePlacementsColumns,
      DataTableProps: {
        includes: IncludesByMode.joborder,
        apiVersion: 2
      }
    }
  },
  {
    id: TabKeys.Candidates,
    label: 'Candidates',
    endpoint: Endpoints.Candidates,
    Component: CandidatesTab,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersCandidate,
      initialColumns: candidateColumns,
      extraRenderers: CustomCandidateRenders,
      DataTableProps: {
        includes: 'personalInformation,specialty,blueSheets,lastActivityLog,keywordTags',
        virtualProps: candidateVirtualProps
      }
    }
  },
  {
    id: TabKeys.HiringAuthorities,
    label: 'Hiring Authorities',
    endpoint: Endpoints.HiringAuthorities,
    Component: HiringAuthorityTab,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersHiringAuthority,
      initialColumns: jobOrderProfileHAColumns,
      DataTableProps: {
        virtualProps: HiringAuthorityVirtualProps,
        apiVersion: 1,
        includes: 'lastActivityLog,specialty,subspecialty,position,phones,status,subStatus',
        formatResponse: handleHiringAuthorityResponse
      }
    }
  },
  {
    id: TabKeys.Tasks,
    label: 'Action Plan',
    Component: TasksTab,
    visible: () =>
      userHasPermission(Permissions.DailyPlan.CanUseModule) &&
      hasFeatureFlag(FeatureFlags.DailyPlanAddTaskFromProfile),
    props: {
      allowedTaskTypes: [TASK_TYPES.recruitJobOrder],
      DataTableProps: {
        apiVersion: 1,
        endpoint: ENDPOINTS.tasks,
        formatResponse: formatTasksResponse
      },
      entityType: EntityType.Joborder,
      entityTypeId: ENTITY_TYPE_ID_MAP.jobOrder,
      extraRenderers: TASKS_EXTRA_RENDERERS,
      initialColumns: TASK_COLUMNS,
      tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrderTasks
    }
  }
];
